import { BaseFeature } from '../../core/base-feature.js';

/**
 * QuizFeature - Frontend feature for Quiz punishment
 * Allows admin to create templates, trigger quizzes, and monitor status on desktop
 */
export class QuizFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.activeQuiz = null;
        this.templates = [];
        this.sessions = [];

        // Event callbacks for React components
        this.quizStateUpdateCallbacks = new Set();
        this.initialStateCallbacks = new Set();
        this.updateCallbacks = new Set();

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'quiz',
            name: 'quiz',
            version: 'v1.0',
            tier: 'external',
            capabilities: [
                'start',
                'cancel',
                'get_state',
                'get_templates',
                'get_template',
                'save_template',
                'update_template',
                'delete_template',
                'get_sessions',
                'get_session_detail'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Quiz] Initializing...');
        console.log('[Quiz] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId, fromId } = message;
        const command = cmd || action;

        switch (command) {
            // Real-time updates
            case 'state_update':
                return this.handleStateUpdate(payload);

            case 'initial_state':
                return this.handleInitialState(payload);

            // Responses to commands
            case 'start_response':
                return this.handleResponse('start', payload);

            case 'cancel_response':
                return this.handleResponse('cancel', payload);

            case 'get_state_response':
                return this.handleResponse('get_state', payload);

            case 'get_templates_response':
                return this.handleResponse('get_templates', payload);

            case 'get_template_response':
                return this.handleResponse('get_template', payload);

            case 'save_template_response':
                return this.handleResponse('save_template', payload);

            case 'update_template_response':
                return this.handleResponse('update_template', payload);

            case 'delete_template_response':
                return this.handleResponse('delete_template', payload);

            case 'get_sessions_response':
                return this.handleResponse('get_sessions', payload);

            case 'get_session_detail_response':
                return this.handleResponse('get_session_detail', payload);

            default:
                console.warn(`[Quiz] Unknown command: ${command}`);
        }
    }

    // =====================================
    // Real-time Event Handlers
    // =====================================

    handleStateUpdate(payload) {
        this.activeQuiz = payload;
        this.notifyQuizStateUpdateListeners(payload);
        this.notifyUpdateListeners();
    }

    handleInitialState(payload) {
        console.log('[Quiz] Received initial state');
        if (payload.templates) {
            this.templates = payload.templates;
        }
        if (payload.activeQuiz !== undefined) {
            this.activeQuiz = payload.activeQuiz;
        }
        if (payload.sessions) {
            this.sessions = payload.sessions;
        }
        this.notifyInitialStateListeners(payload);
        this.notifyUpdateListeners();
    }

    // =====================================
    // Generic Response Handler
    // =====================================

    handleResponse(requestType, payload) {
        const resolve = this._pendingRequests.get(requestType);
        if (resolve) {
            this._pendingRequests.delete(requestType);
            resolve(payload);
        }
    }

    // =====================================
    // Command Methods
    // =====================================

    /**
     * Start a quiz session
     * @param {Object} config - Quiz configuration
     * @returns {Promise<Object>}
     */
    async startQuiz(config) {
        return new Promise((resolve) => {
            this.sendMessage('start', config);
            this._pendingRequests.set('start', resolve);
        });
    }

    /**
     * Cancel active quiz
     * @returns {Promise<Object>}
     */
    async cancelQuiz() {
        return new Promise((resolve) => {
            this.sendMessage('cancel', {});
            this._pendingRequests.set('cancel', resolve);
        });
    }

    /**
     * Get current quiz state
     * @returns {Promise<Object>}
     */
    async getState() {
        return new Promise((resolve) => {
            this.sendMessage('get_state', {});
            this._pendingRequests.set('get_state', resolve);
        });
    }

    /**
     * Get all templates
     * @returns {Promise<Object>}
     */
    async getTemplates() {
        return new Promise((resolve) => {
            this.sendMessage('get_templates', {});
            this._pendingRequests.set('get_templates', resolve);
        });
    }

    /**
     * Get a specific template
     * @param {string} templateId
     * @returns {Promise<Object>}
     */
    async getTemplate(templateId) {
        return new Promise((resolve) => {
            this.sendMessage('get_template', { templateId });
            this._pendingRequests.set('get_template', resolve);
        });
    }

    /**
     * Save a new template
     * @param {Object} templateData
     * @returns {Promise<Object>}
     */
    async saveTemplate(templateData) {
        return new Promise((resolve) => {
            this.sendMessage('save_template', templateData);
            this._pendingRequests.set('save_template', resolve);
        });
    }

    /**
     * Update an existing template
     * @param {string} templateId
     * @param {Object} updates
     * @returns {Promise<Object>}
     */
    async updateTemplate(templateId, updates) {
        return new Promise((resolve) => {
            this.sendMessage('update_template', { templateId, updates });
            this._pendingRequests.set('update_template', resolve);
        });
    }

    /**
     * Delete a template
     * @param {string} templateId
     * @returns {Promise<Object>}
     */
    async deleteTemplate(templateId) {
        return new Promise((resolve) => {
            this.sendMessage('delete_template', { templateId });
            this._pendingRequests.set('delete_template', resolve);
        });
    }

    /**
     * Get session history
     * @param {number} limit
     * @returns {Promise<Object>}
     */
    async getSessions(limit) {
        return new Promise((resolve) => {
            this.sendMessage('get_sessions', { limit });
            this._pendingRequests.set('get_sessions', resolve);
        });
    }

    /**
     * Get detail for a specific session
     * @param {string} sessionId
     * @returns {Promise<Object>}
     */
    async getSessionDetail(sessionId) {
        return new Promise((resolve) => {
            this.sendMessage('get_session_detail', { sessionId });
            this._pendingRequests.set('get_session_detail', resolve);
        });
    }

    // =====================================
    // Event Subscriptions (for React)
    // =====================================

    /**
     * Subscribe to quiz state updates
     */
    onQuizStateUpdate(callback) {
        this.quizStateUpdateCallbacks.add(callback);
        return () => this.quizStateUpdateCallbacks.delete(callback);
    }

    /**
     * Subscribe to initial state events
     */
    onInitialState(callback) {
        this.initialStateCallbacks.add(callback);
        return () => this.initialStateCallbacks.delete(callback);
    }

    /**
     * Subscribe to general updates
     */
    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    notifyQuizStateUpdateListeners(data) {
        for (const callback of this.quizStateUpdateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Quiz] Error in quiz state update callback:', error);
            }
        }
    }

    notifyInitialStateListeners(data) {
        for (const callback of this.initialStateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Quiz] Error in initial state callback:', error);
            }
        }
    }

    notifyUpdateListeners() {
        const data = {
            activeQuiz: this.activeQuiz,
            templates: this.templates,
            sessions: this.sessions
        };
        for (const callback of this.updateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Quiz] Error in update callback:', error);
            }
        }
    }

    // =====================================
    // Getters
    // =====================================

    getActiveQuiz() {
        return this.activeQuiz;
    }

    getTemplateList() {
        return this.templates;
    }

    getSessionList() {
        return this.sessions;
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[Quiz] Cleaning up...');
        this.activeQuiz = null;
        this.templates = [];
        this.sessions = [];
        this.quizStateUpdateCallbacks.clear();
        this.initialStateCallbacks.clear();
        this.updateCallbacks.clear();
        this._pendingRequests.clear();
    }
}
